/**
 * @file ProductKindFormFields.tsx
 * @description Поля формы вида товара (phase19.22, W22) по контракту
 * `value/onChange` — оболочку, сохранение и ошибки сервера держит
 * `ProductKindForm`.
 *
 * Группы — как в макете §4: название → «Срок годности» → «Этикетка» →
 * «Правила» → описание. Срок по умолчанию неактивен, пока вид не требует
 * срока (W20); рядом пересчёт дней в месяцы.
 */

import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { SectionLabel } from "../../ui/SectionLabel";
import { SelectField } from "../../ui/SelectField";
import { Switch } from "../../ui/switch";
import { Textarea } from "../../ui/textarea";
import { vatRateOptions } from "../../../lib/product-kind-format";
import { DaysMonthsField } from "./DaysMonthsField";
import { LabelTemplateSegmented } from "./LabelTemplateSegmented";
import type { ProductKindFormErrors, ProductKindFormValue } from "./product-kind-form";
import { RegulationFields } from "./RegulationFields";

interface Props {
  value: ProductKindFormValue;
  errors: ProductKindFormErrors;
  onChange: (patch: Partial<ProductKindFormValue>) => void;
  disabled?: boolean;
}

export function ProductKindFormFields({ value, errors, onChange, disabled }: Props) {
  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="pk-name">Название *</Label>
        <Input
          id="pk-name"
          value={value.name}
          maxLength={80}
          onChange={(e) => onChange({ name: e.target.value })}
          disabled={disabled}
          aria-invalid={errors.name ? true : undefined}
          placeholder="Например: Пищевой, Сладости, Мёд"
          className="min-h-12"
        />
        {errors.name ? <p className="text-xs text-destructive">{errors.name}</p> : null}
      </div>

      <div className="flex flex-col gap-3">
        <SectionLabel>Срок годности</SectionLabel>
        <SwitchRow
          id="pk-expiry"
          label="Требуется срок"
          hint="Партия без срока будет помечена «срок не указан»."
          checked={value.requiresExpiry}
          onChange={(requiresExpiry) => onChange({ requiresExpiry })}
          disabled={disabled}
        />
        <div className="flex flex-col gap-1.5">
          {/* Дни и месяцы — два способа ввода одного значения в днях (W20). */}
          <DaysMonthsField
            id="pk-shelf"
            label="По умолчанию"
            value={value.requiresExpiry ? value.defaultShelfLifeDays : ""}
            onChange={(defaultShelfLifeDays) => onChange({ defaultShelfLifeDays })}
            maxDays={3660}
            daysPlaceholder={value.requiresExpiry ? "365" : "Не нужен"}
            monthsPlaceholder={value.requiresExpiry ? "12" : "Не нужен"}
            disabled={disabled || !value.requiresExpiry}
            invalid={Boolean(errors.defaultShelfLifeDays)}
          />
          {errors.defaultShelfLifeDays ? (
            <p className="text-xs text-destructive">{errors.defaultShelfLifeDays}</p>
          ) : (
            <p className="text-xs text-on-surface-variant">
              Подставится в приёмке, если известна только дата изготовления. Месяц — 30 дней.
            </p>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <SectionLabel>Этикетка</SectionLabel>
        <LabelTemplateSegmented
          value={value.labelTemplate}
          onChange={(labelTemplate) => onChange({ labelTemplate })}
          disabled={disabled}
        />
      </div>

      <div className="flex flex-col gap-3">
        <SectionLabel>Правила</SectionLabel>
        <RegulationFields
          regulation={value.labelingRegulation}
          note={value.labelingRegulationNote}
          noteError={errors.labelingRegulationNote}
          onChange={onChange}
          disabled={disabled}
        />
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="pk-vat">Ставка НДС по умолчанию</Label>
          <SelectField
            id="pk-vat"
            ariaLabel="Ставка НДС по умолчанию"
            value={value.defaultVatRate}
            onChange={(defaultVatRate) => onChange({ defaultVatRate })}
            options={vatRateOptions(value.defaultVatRate)}
            disabled={disabled}
          />
        </div>
        <SwitchRow
          id="pk-return"
          label="Возврат в остаток разрешён"
          hint="Иначе возврат покупателя — только списание или карантин."
          checked={value.returnToStockAllowed}
          onChange={(returnToStockAllowed) => onChange({ returnToStockAllowed })}
          disabled={disabled}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="pk-desc">Описание</Label>
        <Textarea
          id="pk-desc"
          rows={2}
          value={value.description}
          onChange={(e) => onChange({ description: e.target.value })}
          disabled={disabled}
          placeholder="Для сотрудников — ни на что не влияет"
        />
        {errors.description ? (
          <p className="text-xs text-destructive">{errors.description}</p>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Строка «подпись + переключатель» высотой ≥ 48px. Подпись связана с
 * переключателем через `htmlFor` (как в `CarrierMainFields`) — обёртка
 * `<label>` вокруг кнопки-переключателя давала двойное срабатывание.
 */
function SwitchRow({
  id,
  label,
  hint,
  checked,
  onChange,
  disabled,
}: {
  id: string;
  label: string;
  hint: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex min-h-12 items-center justify-between gap-3 rounded-xl bg-surface-low px-3 py-2">
      <div className="flex min-w-0 flex-col">
        <Label htmlFor={id}>{label}</Label>
        <span className="text-xs text-on-surface-variant">{hint}</span>
      </div>
      <Switch
        id={id}
        checked={checked}
        onCheckedChange={(next) => onChange(Boolean(next))}
        disabled={disabled}
      />
    </div>
  );
}
